import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  APP_STORE_URL,
  buildPlayStoreUrlWithInviteRef,
  detectInvitePlatform,
  parseInviteRefFromSearch,
} from '../../constants/inviteLinks';
import { persistPendingInviteRef } from '../../services/referralInviteService';

/**
 * Canonical peer-invite landing: stores `ref`, then routes by platform.
 * Marketing ads keep using `/download-android` (UTM only).
 */
const Invite: React.FC = () => {
  const { search } = useLocation();
  const navigate = useNavigate();
  const ref = useMemo(() => parseInviteRefFromSearch(search), [search]);
  const platform = useMemo(
    () => (typeof navigator !== 'undefined' ? detectInvitePlatform(navigator.userAgent) : 'desktop'),
    [],
  );
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    if (ref) persistPendingInviteRef(ref);
  }, [ref]);

  useEffect(() => {
    if (!ref || platform !== 'android') return;

    setRedirecting(true);
    const playUrl = buildPlayStoreUrlWithInviteRef(ref);
    const timer = window.setTimeout(() => {
      window.location.replace(playUrl);
    }, 1200);
    return () => window.clearTimeout(timer);
  }, [ref, platform]);

  const signupHref = ref ? `/signup?ref=${encodeURIComponent(ref)}` : '/signup';

  if (!ref) {
    return (
      <div className="min-h-screen bg-[#1A2B4C] flex flex-col items-center justify-center px-6 text-center">
        <p className="text-white text-lg font-semibold mb-4">رابط الدعوة غير صالح</p>
        <p className="text-white/70 text-sm mb-6">Invalid invite link — missing invitation code.</p>
        <Link
          to="/"
          className="inline-block px-6 py-3 rounded-full bg-[#C9A14B] text-[#1A2B4C] font-bold"
        >
          الصفحة الرئيسية / Home
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1A2B4C] flex flex-col items-center justify-center px-6 text-center">
      <div className="w-12 h-12 border-4 border-[#C9A14B] border-t-transparent rounded-full animate-spin mb-6" />
      <h1 className="text-white text-xl font-bold mb-2">مرحباً بك في مرافعة</h1>
      <p className="text-[#C9A14B] text-sm mb-2">Welcome to Morafaa</p>
      <p className="text-white/80 text-sm mb-4 max-w-md">
        كود الدعوة: <span className="font-mono text-[#C9A14B]">{ref}</span>
      </p>

      {platform === 'android' && (
        <>
          <p className="text-white text-base mb-4">
            {redirecting
              ? 'جاري التحويل إلى Google Play…'
              : 'Opening Google Play…'}
          </p>
          <a
            href={buildPlayStoreUrlWithInviteRef(ref)}
            className="inline-block px-6 py-3 rounded-full bg-[#C9A14B] text-[#1A2B4C] font-bold hover:opacity-90 mb-3"
          >
            حمّل تطبيق أندرويد
          </a>
        </>
      )}

      {platform === 'ios' && (
        <>
          <p className="text-white text-base mb-4 max-w-md">
            تطبيق iOS قريباً — يمكنك التسجيل عبر الموقع الآن وإدخال كود الدعوة.
          </p>
          <a
            href={APP_STORE_URL}
            className="inline-block px-6 py-3 rounded-full border border-[#C9A14B] text-[#C9A14B] font-bold mb-3"
          >
            App Store (قريباً)
          </a>
        </>
      )}

      {platform === 'desktop' && (
        <p className="text-white text-base mb-4 max-w-md">
          اختر طريقة الانضمام: تطبيق أندرويد أو التسجيل عبر الموقع.
        </p>
      )}

      <div className="flex flex-col sm:flex-row gap-3 mt-2">
        {platform !== 'android' && (
          <a
            href={buildPlayStoreUrlWithInviteRef(ref)}
            className="inline-block px-6 py-3 rounded-full bg-[#C9A14B] text-[#1A2B4C] font-bold hover:opacity-90"
          >
            Google Play (Android)
          </a>
        )}
        <button
          type="button"
          onClick={() => navigate(signupHref)}
          className="inline-block px-6 py-3 rounded-full border border-white text-white font-bold hover:bg-white/10"
        >
          التسجيل عبر الموقع / Web signup
        </button>
      </div>

      <p className="text-white/50 text-xs mt-6 max-w-sm">
        إذا سجّلت لاحقاً بدون هذا الرابط، أدخل كود الدعوة في خانة «الكود المرجعي».
      </p>
    </div>
  );
};

export default Invite;
